import { defineMessages, IntlShape } from 'dibs-react-intl';
import { graphql, readInlineData, FragmentRef } from 'react-relay';
import { sessionStorage } from 'dibs-browser-storage';

const BANNER_CLOSED_MANUALLY = 'bannerClosedManually';

export const isMessageBannerClosedManually = (): boolean =>
    sessionStorage.getItem(BANNER_CLOSED_MANUALLY) as boolean;

export const setIsBannerClosedManuallyInStorage = (): void => {
    sessionStorage.setItem(BANNER_CLOSED_MANUALLY, true);
};

// migrated methods from `./messageHelpers.js` instead of needing to type the entire file

import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import { shouldShowExpiringPromo } from './expiringPromoMessageHelpers';

import { MessageBannerCarousel_messages } from '../__generated__/MessageBannerCarousel_messages.graphql';
import { sharedMessageHelpers_messages } from './__generated__/sharedMessageHelpers_messages.graphql';
import { sharedMessageHelpers_user } from './__generated__/sharedMessageHelpers_user.graphql';

type Messages = NonNullable<ArrayElement<MessageBannerCarousel_messages>>;
type MessageItems = NonNullable<ArrayElement<Messages['items']>>;
export type MessageItem = NonNullable<MessageItems>;
export type MessageSet = {
    items: {
        note: NonNullNote;
    }[];
}[];

type NonNullNote = NonNullable<MessageItem['note']>;
export type NoteObj = { note: NonNullNote };
type Anchors = NonNullable<NonNullNote['anchors']>;
export type Anchor = ArrayElement<Anchors>;

type MessageItemTemplateData = Pick<
    NonNullable<NonNullNote['content']>,
    'cta' | 'message' | 'title'
> | null;

export type MessageItemTemplate = {
    desktop?: MessageItemTemplateData;
    mobile?: MessageItemTemplateData;
};

export const messageItemTemplate = ({
    desktop = null,
    mobile = null,
}: MessageItemTemplate): NoteObj => {
    return {
        note: {
            serviceId: 'template_id_1',
            content: desktop,
            anchors: [
                {
                    serviceId: 'template_id_2',
                    content: mobile,
                },
            ],
        },
    };
};

export const getMessageTitle = (messageItem: MessageItem): string =>
    messageItem?.note?.content?.title || '';

type TrackObjArgs = {
    messageItem: MessageItem;
    action: string;
    label?: string;
};

type TrackObj = {
    category: 'promo interaction';
    action: string;
    label: string;
};

export const getTrackingObj = ({ messageItem, action, label }: TrackObjArgs): TrackObj => {
    return {
        category: 'promo interaction',
        action,
        label: label || getMessageTitle(messageItem),
    };
};

export const isLowResolution = typeof window !== 'undefined' && window.innerWidth < 568; // To match scss mobile breakpoint size.

export type Cta = {
    text: string;
    url: string;
};

type NoteByDevice = NonNullable<ArrayElement<sharedMessageHelpers_messages['items']>>['note'];
type NoteByDeviceNote = {
    note: {
        content: { message: string | null; title?: string | null; cta?: Cta | null } | null;
    };
};
type NoteByDeviceItem = {
    note: NoteByDevice;
};

export const getNoteByDevice = (
    messageItem: NoteByDeviceItem | null
): NoteByDeviceNote['note'] | null | undefined => {
    if (isLowResolution) {
        return messageItem?.note?.anchors?.[0];
    }
    return messageItem?.note;
};

const messagesFragment = graphql`
    fragment sharedMessageHelpers_messages on MessageSetModule @inline {
        items {
            note {
                serviceId
                content {
                    message
                }
                anchors {
                    content {
                        message
                    }
                }
            }
        }
    }
`;

const userActivePromotionFragment = graphql`
    fragment sharedMessageHelpers_user on User @inline {
        ...expiringPromoMessageHelpers_user
        ...ExpiringPromoMessage_user
        activePromotion {
            promotionMeta {
                customBannerMessage
                desktopBannerMessage
                desktopBannerCta
                mobileBannerMessage
                mobileBannerCta
            }
        }
    }
`;

export const hasAnyText = (messageItem: NoteByDeviceItem): boolean => {
    const noteByDevice = getNoteByDevice(messageItem);
    return !!noteByDevice?.content?.message;
};

export const hasAnyMessagesByDevice = (
    messageSetRef: FragmentRef<sharedMessageHelpers_messages>[] = [],
    userRef: FragmentRef<sharedMessageHelpers_user>
): boolean => {
    let hasPromoMessageByDevice = false;
    if (userRef) {
        const userData =
            readInlineData<sharedMessageHelpers_user>(userActivePromotionFragment, userRef) || {};
        const { promotionMeta } = userData?.activePromotion || {};
        const { desktopBannerMessage, mobileBannerMessage } = promotionMeta || {};
        hasPromoMessageByDevice =
            // `shouldShowExpiringPromo(userData)` seems like it will always return false
            // b/c it only looks for `expiringPromotion` and this query only has `activePromotion`
            shouldShowExpiringPromo(userData) ||
            (isLowResolution ? !!mobileBannerMessage : !!desktopBannerMessage);
    }
    return (
        hasPromoMessageByDevice ||
        messageSetRef.some(messageSetRefItem => {
            const { items } = readInlineData<sharedMessageHelpers_messages>(
                messagesFragment,
                messageSetRefItem
            );
            return (items || []).filter(filterNulls).some(messageItem => hasAnyText(messageItem));
        })
    );
};

const fallbackText = defineMessages({
    fallback: {
        id: 'dbl.Header.MessageBanner.global_fallback_text',
        defaultMessage: "1stDibs is the world's leading marketplace for beautiful and rare objects",
    },
});
const globalFallbackMessage = (intl: IntlShape): NoteObj => {
    const content = {
        title: 'global fallback',
        message: intl.formatMessage(fallbackText.fallback),
        cta: null,
    };
    return messageItemTemplate({
        desktop: content,
        mobile: content,
    });
};

export const getFallbackMessagesArray = (intl: IntlShape, messageSet: MessageSet): NoteObj[] => {
    const fallback = (messageSet?.[2]?.items || [])
        .filter(filterNulls)
        .filter(item => hasAnyText(item));
    if (!fallback.length) {
        return [globalFallbackMessage(intl)];
    }
    return fallback;
};
